import { lastUtils } from '@last/core'

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

type Cookies = {
  essential?: boolean
  analysis?: boolean
  advertising?: boolean
}

type Options = {
  cookies: Cookies
  proxyUrl?: string
  conversionKey?: string
  domains?: string[]
}

type Product = {
  id: string | number
  name: string
  quantity: number
  finalPrice: number
}

export default class GoogleAds {
  private googleAdsId: string
  private conversionKey?: string

  constructor(googleAdsId: string, options: Options) {
    this.googleAdsId = googleAdsId
    this.conversionKey = options.conversionKey

    window.dataLayer = window.dataLayer || []
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments)
    }

    if (!options.cookies.analysis && !options.domains) return

    const ids = googleAdsId.split(',')
    const primaryId = ids[0]

    const script = document.createElement('script')
    script.async = true
    script.src = options.proxyUrl || `https://www.googletagmanager.com/gtag/js?id=${primaryId}`
    document.head.appendChild(script)

    window.gtag('js', new Date())
    const config: Record<string, unknown> = { allow_enhanced_conversions: true }
    if (options.domains) config.linker = { domains: options.domains }
    window.gtag('config', primaryId, config)
    for (const extraId of ids.slice(1)) {
      window.gtag('config', extraId)
    }
  }

  isAvailable() {
    return true
  }

  mapProducts(products: Product[]) {
    return products.map(product => ({
      id: product.id,
      item_id: product.id,
      name: product.name,
      item_name: product.name,
      quantity: product.quantity,
      price: lastUtils.round(product.finalPrice / 100)
    }))
  }

  checkoutCompleted({
    orderId,
    locationName,
    products,
    totalPrice,
    deliveryFee,
    taxRate,
    currencyCode,
    promotionCode
  }: {
    orderId: string
    locationName: string
    products: Product[]
    totalPrice: number
    deliveryFee: number
    taxRate: number
    currencyCode?: string
    promotionCode?: string
  }) {
    window.gtag('event', 'purchase', {
      transaction_id: orderId,
      affiliation: locationName,
      value: lastUtils.round(totalPrice / 100),
      currency: currencyCode || 'EUR',
      tax: lastUtils.round((totalPrice - totalPrice / (1 + taxRate)) / 100),
      shipping: lastUtils.round(deliveryFee / 100),
      items: this.mapProducts(products),
      coupon: promotionCode
    })
  }

  addProductToCart({ product }: { product: Product }) {
    window.gtag('event', 'add_to_cart', {
      items: this.mapProducts([product])
    })
  }

  removeProductFromCart({ product }: { product: Product }) {
    window.gtag('event', 'remove_from_cart', {
      items: this.mapProducts([product])
    })
  }

  beginCheckout({
    products,
    totalPrice,
    currencyCode,
    promotionCode
  }: {
    products: Product[]
    totalPrice: number
    currencyCode?: string
    promotionCode?: string
  }) {
    window.gtag('event', 'begin_checkout', {
      currency: currencyCode || 'EUR',
      value: lastUtils.round(totalPrice / 100),
      items: this.mapProducts(products),
      coupon: promotionCode
    })
  }

  userLoggedIn() {
    window.gtag('event', 'login')
  }

  userSignedUp() {
    window.gtag('event', 'sign_up')
  }

  itemViewed({ item, currencyCode }: { item: Product; currencyCode?: string }) {
    const products = this.mapProducts([item])
    window.gtag('event', 'view_item', {
      currency: currencyCode || 'EUR',
      value: products[0].price,
      items: products
    })
  }

  bookingOnGoing() {
    window.gtag('event', 'booking_on_going')
  }

  bookingDone() {
    window.gtag('event', 'booking_done')
  }

  autoOnboardingDone() {
    this.fireConversion()
  }

  newLead() {
    this.fireConversion()
    if (!this.conversionKey) return
    const script = document.createElement('script')
    script.defer = true
    script.src = 'https://pulse.clickguard.com/sc'
    document.head.appendChild(script)
  }

  private fireConversion() {
    if (!this.conversionKey) return
    window.gtag('event', 'conversion', {
      send_to: `${this.googleAdsId}/${this.conversionKey}`
    })
  }
}
